// Centralized pricing copy.
//
// Before this file the same pricing sentence was written five different ways
// across pricing.tsx, home.tsx, solutions.tsx, seo.ts, auth-layout.tsx, and
// docs.ts, and they drifted. Every page now reads from here, so the pricing
// story is changed in exactly one place.
//
// Rule for anything added here: it must be TRUE. Do not state a statistic the
// product cannot substantiate from its own data — see DONOR_COVERAGE_STAT.

/** The single sentence that defines what KindLumen costs an organization. */
export const PLATFORM_FEE_STATEMENT =
  'KindLumen charges organizations no subscription fees, setup fees, or platform fees.'

/** Short trust chip, for hero rows and auth screens. */
export const ZERO_FEE_BADGE = '$0 platform fees for organizations'

/**
 * The no-limits claim, in the vocabulary form-builder shoppers compare on
 * ("forms", "responses"). True by construction: the product enforces no count
 * limits on forms, campaigns, donations, or donors anywhere in the codebase.
 * If a plan limit is ever introduced, this statement must change in the same
 * commit.
 */
export const UNLIMITED_STATEMENT =
  'Unlimited donation forms, campaigns, donations, and donors. No response caps or monthly submission limits.'

/**
 * Distinguishes optional fee coverage from tip-jar monetization (Zeffy,
 * Givebutter). Donors see transparent fee coverage, never a tip prompt for
 * KindLumen.
 */
export const NO_TIPS_STATEMENT = 'Donors are never asked to tip KindLumen.'

/**
 * What happens when a donor declines to cover costs. Stated explicitly
 * everywhere the "free" claim appears — payment processing is a real cost and
 * it does not vanish, it is simply never charged by KindLumen.
 */
export const PROCESSING_FEE_DISCLOSURE =
  'If a donor does not cover transaction costs, the selected payment provider’s processing fees are deducted from the donation. KindLumen still charges the organization nothing.'

/**
 * How to describe the optional transaction-cost choice.
 *
 * `verified` MUST stay false until this can be substantiated from real
 * KindLumen donations. Coverage only began being recorded per-donation with
 * the free-for-organizations release (donations.donorCoversCosts), so there is
 * no history to draw on and the honest copy makes no adoption-rate claim.
 *
 * To publish a number later: read `coverageSelectionRate` from
 * computeBillingOverview (convex/lib/presenters.ts) across a meaningful sample,
 * then set `verified: true` and fill in `range`. `statement` switches
 * automatically — no page edits.
 */
export const DONOR_COVERAGE_STAT = {
  verified: false,
  /** Only used once `verified` is true. */
  range: { low: 60, high: 70 },
  get statement() {
    return this.verified
      ? `Typically, ${this.range.low}–${this.range.high}% of donors choose to cover transaction costs, helping organizations keep more of every donation.`
      : 'Donors can choose to cover transaction costs. The added amount uses the configured processing-rate estimate with the aim of preserving the intended gift.'
  },
} as const

export const FREE_PLAN = {
  name: 'Free',
  price: '$0',
  cadence: 'per month',
  features: [
    'No setup fee',
    'No monthly fee',
    'No contracts',
    'No platform fee',
    'Unlimited donation forms and campaigns',
    'Unlimited donations and donors — no response caps',
    'Donors may optionally cover transaction costs',
    'Transaction-cost coverage is calculated to help preserve the intended donation',
  ],
  cta: { label: 'Start for Free', href: '/signup' },
  clarification: PROCESSING_FEE_DISCLOSURE,
} as const

export const PRICING_FAQ = [
  {
    question: 'Is KindLumen really free?',
    answer: 'Yes. KindLumen does not charge organizations setup fees, subscription fees, or platform fees.',
  },
  {
    question: 'How does KindLumen make money?',
    answer:
      'Donors may choose to cover transaction costs. When the connected provider supports automatic collection, that additional amount includes a disclosed 1% contribution that helps operate KindLumen. KindLumen collects no contribution when automatic collection is unavailable.',
  },
  {
    question: 'What happens if a donor does not cover transaction costs?',
    answer:
      'The donor pays only their selected donation amount. The selected payment provider deducts its processing fee from the donation, and KindLumen receives nothing.',
  },
  {
    question: 'Does the organization receive the full donation?',
    answer:
      'When a donor covers transaction costs, the added amount is calculated from the configured processing-rate estimate with the aim of preserving the selected donation. Actual provider fees can vary by account, payment method, and currency. Without coverage, the provider deducts its processing fee from the donation.',
  },
  {
    question: 'Is the donor contribution optional?',
    answer: 'Yes. Donors can choose whether to cover transaction costs before completing their donation.',
  },
  {
    question: 'Are there limits on forms, donations, or donors?',
    answer:
      'No. KindLumen has no limits on the number of donation forms, campaigns, donations, or donors, and it has no response caps or monthly submission limits.',
  },
  {
    question: 'Do donors have to tip KindLumen?',
    answer:
      'No. Donors are never asked to tip KindLumen. Donors may optionally cover transaction costs, and the amount is shown transparently before they complete their donation.',
  },
  {
    question: 'When do donations reach my bank account?',
    answer:
      'Donations are processed through your connected Stripe, Square, or PayPal account; KindLumen does not hold the funds or add a payout schedule. Your provider sends funds to your bank according to its account-specific payout timing and availability rules.',
  },
] as const
